import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGetIdentity, useNotify } from "ra-core";
import { Link, useNavigate, useSearchParams } from "react-router";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, ExternalLink, List, MapPin, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { useSwipe } from "../misc/useSwipe";

// LDF: Eventkalender – Berliner Markt-Events als Akquise-Anlässe.

export type MarketEvent = {
  id: number;
  name: string;
  category: string | null;
  recurring: string | null;
  start_date: string | null;
  end_date: string | null;
  date_text: string | null;
  date_confidence: string | null;
  last_edition_date: string | null;
  location: string | null;
  location_address: string | null;
  location_company_id: number | null;
  organiser: string | null;
  organiser_company_id: number | null;
  agency: string | null;
  agency_company_id: number | null;
  guests: string | null;
  relevance: string | null;
  source_urls: string[] | null;
  confidence: string | null;
  status: string;
  notes: string | null;
  deal_id: number | null;
};

// Nationalfeiertage der Botschaften – nur bei Bedarf einblenden (wir arbeiten nicht ständig mit Botschaften)
const NATIONAL_DAYS_KEY = "ldf.events.national-days";
const isNationalDay = (e: MarketEvent) => /^nationalfeiertag/i.test(e.name) || (e.category === "empfang" && /^botschaft/i.test(e.organiser ?? ""));

const CATEGORIES: Record<string, string> = {
  award: "Award",
  gala: "Gala",
  charity: "Charity",
  ball: "Ball",
  premiere: "Premiere",
  presse: "Presse",
  sommerfest: "Sommerfest",
  empfang: "Empfang",
  fashion: "Fashion",
  sport: "Sport",
  kongress: "Kongress",
  messe: "Messe",
  firmenfeier: "Firmenfeier",
  sonstiges: "Sonstiges",
};

const STATUSES: Record<string, string> = {
  beobachten: "Beobachten",
  ansprechen: "Ansprechen",
  angefragt: "Angefragt",
  gewonnen: "Gewonnen",
  "kein-thema": "Kein Thema",
};

type Scope = "kommend" | "offen" | "vergangen" | "alle";

const today = () => new Date().toISOString().slice(0, 10);

const fmt = (d: string | null) =>
  d ? new Date(`${d}T12:00:00`).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";

// Datum für Sortierung/Gruppierung: bestätigtes Datum, sonst erwarteter Termin im Folgejahr der letzten Ausgabe
const planDate = (e: MarketEvent): string | null => {
  if (e.start_date) return e.start_date;
  if (e.last_edition_date && e.recurring) {
    const d = new Date(`${e.last_edition_date}T12:00:00`);
    while (d.toISOString().slice(0, 10) < today()) d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  }
  return null;
};

const monthLabel = (d: string | null) =>
  d ? new Date(`${d}T12:00:00`).toLocaleDateString("de-DE", { month: "long", year: "numeric" }) : "Ohne Termin";

const EventRow = ({ event, onChanged, defaultOpen = false }: { event: MarketEvent; onChanged: () => void; defaultOpen?: boolean }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [notes, setNotes] = useState(event.notes ?? "");
  const notify = useNotify();
  const navigate = useNavigate();
  const { identity } = useGetIdentity();
  const supabase = getSupabaseClient();
  const plan = planDate(event);
  const confirmed = !!event.start_date;

  const update = async (values: Partial<MarketEvent>, message?: string) => {
    const { error } = await supabase
      .from("market_events")
      .update({ ...values, updated_at: new Date().toISOString() })
      .eq("id", event.id);
    if (error) return notify(`Fehler: ${error.message}`, { type: "error" });
    if (message) notify(message, { type: "success" });
    onChanged();
  };

  const createDeal = async () => {
    const companyId = event.agency_company_id ?? event.organiser_company_id ?? event.location_company_id;
    if (!companyId) {
      notify("Bitte zuerst Veranstalter oder Agentur als Firma verknüpfen (Firma anlegen per Cmd+V in der Firmenliste).", {
        type: "warning",
      });
      return;
    }
    const { data: anfragen } = await supabase.from("deals").select("id,index").eq("stage", "anfrage");
    await Promise.all(
      (anfragen ?? []).map((d) => supabase.from("deals").update({ index: (d.index ?? 0) + 1 }).eq("id", d.id)),
    );
    const description = [
      `Aus Eventkalender: ${event.name}`,
      plan ? `Termin: ${fmt(plan)}${confirmed ? "" : " (erwartet)"}` : event.date_text,
      event.location && `Location: ${event.location}`,
      event.organiser && `Veranstalter: ${event.organiser}`,
      event.agency && `Agentur: ${event.agency}`,
      event.guests && `Gäste: ${event.guests}`,
      event.relevance,
    ]
      .filter(Boolean)
      .join("\n");
    const { data, error } = await supabase
      .from("deals")
      .insert({
        name: event.name,
        company_id: companyId,
        stage: "anfrage",
        index: 0,
        description,
        expected_closing_date: plan,
        contact_ids: [],
        sales_id: identity?.id ?? null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .select("id")
      .single();
    if (error) return notify(`Auftrag konnte nicht angelegt werden: ${error.message}`, { type: "error" });
    await update({ deal_id: data.id, status: "angefragt" });
    notify("Auftrag in der Pipeline angelegt", { type: "success" });
    navigate(`/deals/${data.id}/show`);
  };

  return (
    <div className="border-b border-border py-3">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-start gap-3 text-left cursor-pointer">
        <div className="w-24 shrink-0 text-sm">
          {plan ? (
            <>
              <div className={confirmed ? "font-medium" : "text-muted-foreground"}>{fmt(plan)}</div>
              {!confirmed && <div className="text-[11px] text-muted-foreground">erwartet</div>}
            </>
          ) : (
            <div className="text-xs text-muted-foreground">{event.date_text ?? "offen"}</div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{event.name}</span>
            {event.category && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                {CATEGORIES[event.category] ?? event.category}
              </span>
            )}
            {event.status !== "beobachten" && (
              <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] text-primary">{STATUSES[event.status]}</span>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
            {event.location && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {event.location}
              </span>
            )}
            {event.organiser && <span>{event.organiser}</span>}
            {event.agency && <span>Agentur: {event.agency}</span>}
          </div>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="ml-27 mt-3 space-y-3 pl-0 text-sm sm:ml-27">
          <div className="grid gap-2 sm:grid-cols-2">
            <Detail label="Termin" value={[event.start_date && `${fmt(event.start_date)}${event.end_date ? ` – ${fmt(event.end_date)}` : ""}`, event.date_text, event.date_confidence].filter(Boolean).join(" · ")} />
            <Detail label="Letzte Ausgabe" value={fmt(event.last_edition_date)} />
            <Detail label="Location" value={[event.location, event.location_address].filter(Boolean).join(", ")} />
            <Detail label="Rhythmus" value={event.recurring} />
            <Detail label="Veranstalter" value={event.organiser} />
            <Detail label="Agentur" value={event.agency} />
            <Detail label="Gäste" value={event.guests} />
            <Detail label="Recherche" value={event.confidence} />
          </div>
          {event.relevance && <p className="text-sm">{event.relevance}</p>}
          <div className="flex flex-wrap gap-2 text-xs">
            {[
              ["Location", event.location_company_id],
              ["Veranstalter", event.organiser_company_id],
              ["Agentur", event.agency_company_id],
            ]
              .filter(([, id]) => id)
              .map(([label, id]) => (
                <Link key={String(label)} to={`/companies/${id}/show`} className="rounded-full border border-border px-3 py-1 hover:bg-muted">
                  {label} im CRM öffnen
                </Link>
              ))}
            {event.deal_id && (
              <Link to={`/deals/${event.deal_id}/show`} className="rounded-full border border-primary px-3 py-1 text-primary hover:bg-primary/10">
                Auftrag öffnen
              </Link>
            )}
          </div>
          {event.source_urls?.length ? (
            <div className="space-y-0.5">
              {event.source_urls.map((url) => (
                <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 truncate text-xs underline hover:no-underline">
                  <ExternalLink className="h-3 w-3 shrink-0" />
                  <span className="truncate">{url.replace(/^https?:\/\/(www\.)?/, "")}</span>
                </a>
              ))}
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={event.status}
              onChange={(e) => update({ status: e.target.value }, "Status geändert")}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
            >
              {Object.entries(STATUSES).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {!event.deal_id && (
              <Button size="sm" onClick={createDeal}>
                Anfrage daraus machen
              </Button>
            )}
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => notes !== (event.notes ?? "") && update({ notes }, "Notiz gespeichert")}
            placeholder="Notiz (z. B. wer den Kontakt hat, was bekannt ist)"
            className="min-h-16 w-full rounded-md border border-input bg-background p-2 text-sm"
          />
        </div>
      )}
    </div>
  );
};

const Detail = ({ label, value }: { label: string; value?: string | null }) =>
  value ? (
    <div>
      <span className="block text-xs text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  ) : null;

const NewEventForm = ({ onDone }: { onDone: () => void }) => {
  const notify = useNotify();
  const [values, setValues] = useState({ name: "", start_date: "", location: "", organiser: "", agency: "", category: "", source: "" });
  const set = (k: keyof typeof values) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setValues({ ...values, [k]: e.target.value });
  const save = async () => {
    const { error } = await getSupabaseClient()
      .from("market_events")
      .insert({
        name: values.name,
        start_date: values.start_date || null,
        date_confidence: values.start_date ? "bestätigt" : "unbelegt",
        location: values.location || null,
        organiser: values.organiser || null,
        agency: values.agency || null,
        category: values.category || null,
        source_urls: values.source ? [values.source] : null,
      });
    if (error) return notify(`Fehler: ${error.message}`, { type: "error" });
    notify("Event angelegt", { type: "success" });
    onDone();
  };
  const input = "h-9 rounded-md border border-input bg-background px-3 text-sm";
  return (
    <div className="grid gap-2 rounded-lg border border-dashed border-border p-3 sm:grid-cols-2">
      <input className={input} placeholder="Eventname *" value={values.name} onChange={set("name")} />
      <input className={input} type="date" value={values.start_date} onChange={set("start_date")} title="Eventdatum (nicht Artikeldatum)" />
      <input className={input} placeholder="Location *" value={values.location} onChange={set("location")} />
      <select className={input} value={values.category} onChange={set("category")}>
        <option value="">Kategorie …</option>
        {Object.entries(CATEGORIES).map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
      <input className={input} placeholder="Veranstalter" value={values.organiser} onChange={set("organiser")} />
      <input className={input} placeholder="Agentur" value={values.agency} onChange={set("agency")} />
      <input className={`${input} sm:col-span-2`} placeholder="Quelle (Link)" value={values.source} onChange={set("source")} />
      <div className="flex gap-2 sm:col-span-2">
        <Button size="sm" onClick={save} disabled={!values.name.trim() || !values.location.trim()}>
          Speichern
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Abbrechen
        </Button>
      </div>
    </div>
  );
};

// Eigene Aufträge mit Eventdatum (Angebot, beauftragt, durchgeführt …)
export type CalendarDeal = {
  id: number;
  name: string;
  event_date: string;
  stage: string;
  company: string | null;
  location: string | null;
};

const STAGE_LABELS: Record<string, string> = {
  anfrage: "Anfrage",
  konzept: "Konzept",
  "angebot-versendet": "Angebot versendet",
  verhandlung: "Verhandlung",
  beauftragt: "Beauftragt",
  durchgefuehrt: "Durchgeführt",
  abgerechnet: "Abgerechnet",
  verloren: "Verloren",
};

// Eigene Aufträge immer mit blauem Rahmen, damit sie zwischen den Markt-Events auffallen
const dealChipClass = (stage: string) =>
  stage === "verloren"
    ? "border-2 border-blue-600 bg-muted text-muted-foreground line-through"
    : ["beauftragt", "durchgefuehrt", "abgerechnet"].includes(stage)
      ? "border-2 border-blue-600 bg-blue-600 text-white"
      : "border-2 border-blue-600 bg-blue-50 text-blue-800 dark:bg-blue-950 dark:text-blue-200";

const DealRow = ({ deal }: { deal: CalendarDeal }) => (
  <Link
    to={`/deals/${deal.id}/show`}
    className="my-1 flex items-start gap-3 rounded-md border-2 border-blue-600 px-2 py-3 hover:bg-blue-50 dark:hover:bg-blue-950"
  >
    <div className="w-24 shrink-0 text-sm font-medium">{fmt(deal.event_date)}</div>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{deal.name}</span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] ${dealChipClass(deal.stage)}`}>
          Unser Auftrag · {STAGE_LABELS[deal.stage] ?? deal.stage}
        </span>
      </div>
      <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
        {deal.company && <span>{deal.company}</span>}
        {deal.location && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {deal.location}
          </span>
        )}
      </div>
    </div>
  </Link>
);

// Offene Aufgaben (Nachfassen, Anruf …) am Fälligkeitstag
export type CalendarTask = {
  id: number;
  type: string | null;
  text: string | null;
  due: string;
  contact_id: number;
  who: string;
  owner: string;
};

const TASK_LABELS: Record<string, string> = {
  anruf: "Anruf",
  "e-mail": "E-Mail",
  besuch: "Besuch",
  angebot: "Angebot",
  nachfassen: "Nachfassen",
  grusskarte: "Grußkarte",
  bewertung: "Bewertung",
  sonstiges: "Aufgabe",
};

const taskChipClass = (overdue: boolean) =>
  overdue
    ? "border border-red-500 border-l-4 bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200"
    : "border border-violet-500 border-l-4 bg-violet-50 text-violet-900 dark:bg-violet-950 dark:text-violet-100";

const TaskRow = ({ task }: { task: CalendarTask }) => {
  const overdue = task.due < today();
  return (
    <Link to={`/contacts/${task.contact_id}/show`} className={`my-1 flex items-start gap-3 rounded-md px-2 py-2 hover:opacity-90 ${taskChipClass(overdue)}`}>
      <div className="w-24 shrink-0 text-sm font-medium">{fmt(task.due)}</div>
      <div className="min-w-0 flex-1 text-sm">
        <span className="font-medium">☐ {TASK_LABELS[task.type ?? ""] ?? "Aufgabe"}:</span> {task.text}
        <div className="text-xs opacity-80">
          {task.who} · {task.owner}
          {overdue ? " · überfällig" : ""}
        </div>
      </div>
    </Link>
  );
};

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const MonthGrid = ({
  events,
  deals,
  tasks,
  month,
  onMonth,
  selectedId,
  onSelect,
}: {
  events: MarketEvent[];
  deals: CalendarDeal[];
  tasks: CalendarTask[];
  month: Date;
  onMonth: (d: Date) => void;
  selectedId: number | null;
  onSelect: (id: number | null) => void;
}) => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7; // Montag zuerst
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = Math.ceil((offset + daysInMonth) / 7) * 7;
  const todayIso = today();
  const monthKey = iso(first).slice(0, 7);

  // Events je Tag (mehrtägige an jedem Tag), Datum = bestätigt oder erwartet
  const byDay = new Map<string, MarketEvent[]>();
  const undated: MarketEvent[] = [];
  const tasksByDay = new Map<string, CalendarTask[]>();
  for (const t of tasks) {
    if (t.due.startsWith(monthKey)) tasksByDay.set(t.due, [...(tasksByDay.get(t.due) ?? []), t]);
  }
  const dealsByDay = new Map<string, CalendarDeal[]>();
  for (const d of deals) {
    if (d.event_date.startsWith(monthKey)) dealsByDay.set(d.event_date, [...(dealsByDay.get(d.event_date) ?? []), d]);
  }
  const navigate = useNavigate();
  for (const e of events) {
    const start = planDate(e);
    if (!start) continue;
    if (!e.start_date && !start.startsWith(monthKey)) continue;
    const startD = new Date(`${start}T12:00:00`);
    const endD = e.start_date && e.end_date ? new Date(`${e.end_date}T12:00:00`) : startD;
    for (let d = new Date(startD), i = 0; d <= endD && i < 31; d.setDate(d.getDate() + 1), i++) {
      const key = iso(d);
      if (key.startsWith(monthKey)) byDay.set(key, [...(byDay.get(key) ?? []), e]);
    }
  }
  for (const e of events) {
    if (!planDate(e) && e.date_text && /\d{1,2}\/\d{4}|\d{4}-\d{2}/.test(e.date_text)) {
      const m = e.date_text.match(/(\d{1,2})\/(\d{4})/);
      if (m && Number(m[1]) === month.getMonth() + 1 && Number(m[2]) === month.getFullYear()) undated.push(e);
    }
  }

  const go = (delta: number) => onMonth(new Date(month.getFullYear(), month.getMonth() + delta, 1));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Button size="icon" variant="outline" onClick={() => go(-1)} aria-label="Vorheriger Monat">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-2">
          <h2 className="font-display text-xl font-semibold capitalize">
            {first.toLocaleDateString("de-DE", { month: "long", year: "numeric" })}
          </h2>
          <Button size="sm" variant="ghost" onClick={() => onMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}>
            Heute
          </Button>
        </div>
        <Button size="icon" variant="outline" onClick={() => go(1)} aria-label="Nächster Monat">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="overflow-x-auto">
        <div className="grid min-w-[640px] grid-cols-7 border-l border-t border-border">
          {WEEKDAYS.map((w) => (
            <div key={w} className="border-b border-r border-border bg-muted/50 px-2 py-1 text-xs font-medium text-muted-foreground">
              {w}
            </div>
          ))}
          {Array.from({ length: cells }, (_, i) => {
            const day = i - offset + 1;
            const inMonth = day >= 1 && day <= daysInMonth;
            const key = inMonth ? iso(new Date(month.getFullYear(), month.getMonth(), day)) : "";
            const list = inMonth ? (byDay.get(key) ?? []) : [];
            const ownDeals = inMonth ? (dealsByDay.get(key) ?? []) : [];
            const dayTasks = inMonth ? (tasksByDay.get(key) ?? []) : [];
            const weekend = i % 7 >= 5;
            return (
              <div
                key={i}
                className={`min-h-24 border-b border-r border-border p-1 ${inMonth ? (weekend ? "bg-muted/20" : "") : "bg-muted/40"}`}
              >
                {inMonth && (
                  <div className={`mb-1 text-xs ${key === todayIso ? "inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                    {day}
                  </div>
                )}
                <div className="space-y-0.5">
                  {dayTasks.map((t) => (
                    <button
                      key={`task-${t.id}`}
                      type="button"
                      onClick={() => navigate(`/contacts/${t.contact_id}/show`)}
                      title={`${TASK_LABELS[t.type ?? ""] ?? "Aufgabe"}: ${t.text ?? ""} – ${t.who} (${t.owner})`}
                      className={`block w-full truncate rounded px-1 py-0.5 text-left text-[11px] leading-tight cursor-pointer ${taskChipClass(t.due < todayIso)}`}
                    >
                      ☐ {TASK_LABELS[t.type ?? ""] ?? "Aufgabe"}: {t.who}
                    </button>
                  ))}
                  {ownDeals.map((d) => (
                    <button
                      key={`deal-${d.id}`}
                      type="button"
                      onClick={() => navigate(`/deals/${d.id}/show`)}
                      title={`Unser Auftrag: ${d.name}${d.company ? ` – ${d.company}` : ""}${d.location ? ` – ${d.location}` : ""} (${STAGE_LABELS[d.stage] ?? d.stage})`}
                      className={`block w-full truncate rounded px-1 py-0.5 text-left text-[11px] font-medium leading-tight cursor-pointer ${dealChipClass(d.stage)}`}
                    >
                      ★ {d.name}
                    </button>
                  ))}
                  {list.slice(0, 4).map((e) => (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => onSelect(selectedId === e.id ? null : e.id)}
                      title={`${e.name}${e.location ? ` – ${e.location}` : ""}${e.start_date ? "" : " (erwartet)"}`}
                      className={`block w-full truncate rounded px-1 py-0.5 text-left text-[11px] leading-tight cursor-pointer ${
                        selectedId === e.id
                          ? "bg-primary text-primary-foreground"
                          : e.start_date
                            ? e.status === "kein-thema"
                              ? "bg-muted text-muted-foreground line-through"
                              : "bg-primary/15 text-foreground hover:bg-primary/25"
                            : "border border-dashed border-border text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      {e.name}
                    </button>
                  ))}
                  {list.length > 4 && (
                    <button
                      type="button"
                      className="text-[11px] text-muted-foreground underline cursor-pointer"
                      onClick={() => onSelect(list[4].id)}
                    >
                      +{list.length - 4} weitere
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {undated.length > 0 && (
        <div className="text-xs text-muted-foreground">
          In diesem Monat ohne genauen Tag:{" "}
          {undated.map((e, i) => (
            <button key={e.id} type="button" className="underline cursor-pointer" onClick={() => onSelect(e.id)}>
              {e.name}
              {i < undated.length - 1 ? ", " : ""}
            </button>
          ))}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">
        ☐ lila = offene Aufgabe (rot = überfällig) · ★ blau umrandet = unser Auftrag (blau gefüllt: beauftragt/durchgeführt, hell: Anfrage/Angebot) · Gefüllt = bestätigtes Datum · gestrichelt = erwartet (aus letzter Ausgabe abgeleitet) · Pfeiltasten ← → wechseln den Monat
      </p>
    </div>
  );
};

export const EventCalendar = () => {
  const [scope, setScope] = useState<Scope>("kommend");
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [showNationalDays, setShowNationalDays] = useState(() => {
    try {
      return localStorage.getItem(NATIONAL_DAYS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const toggleNationalDays = () =>
    setShowNationalDays((v) => {
      try {
        localStorage.setItem(NATIONAL_DAYS_KEY, v ? "0" : "1");
      } catch {
        // Speicher nicht verfügbar
      }
      return !v;
    });
  const [adding, setAdding] = useState(false);
  const [view, setView] = useState<"kalender" | "liste">(() => {
    try {
      return (localStorage.getItem("ldf.events.view") as "kalender" | "liste") ?? "kalender";
    } catch {
      return "kalender";
    }
  });
  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  // Ansicht per URL steuerbar (z. B. vom Claude-Chat): ?ansicht=kalender&monat=2026-10&kategorie=award&suche=…&bereich=kommend&event=123
  const [params] = useSearchParams();
  useEffect(() => {
    if (![...params.keys()].some((k) => ["_ansicht", "ansicht", "monat", "kategorie", "suche", "bereich", "event"].includes(k))) return;
    const v = params.get("ansicht");
    if (v === "kalender" || v === "liste") setView(v);
    const m = params.get("monat")?.match(/^(\d{4})-(\d{2})$/);
    if (m) setMonth(new Date(Number(m[1]), Number(m[2]) - 1, 1));
    setCategory(params.get("kategorie") ?? "");
    setSearch(params.get("suche") ?? "");
    const b = params.get("bereich");
    setScope(b === "offen" || b === "vergangen" || b === "alle" ? b : "kommend");
    const e = Number(params.get("event"));
    setSelectedId(e || null);
  }, [params]);

  useEffect(() => {
    try {
      localStorage.setItem("ldf.events.view", view);
    } catch {
      // Speicher nicht verfügbar
    }
  }, [view]);

  // ← → wechseln im Kalender den Monat (nicht beim Tippen in Feldern)
  useEffect(() => {
    if (view !== "kalender") return;
    const handler = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.closest("input, textarea, select, [contenteditable=true]")) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector('[role="dialog"][data-state="open"], [role="menu"], [role="listbox"]')) return;
      if (e.key === "ArrowLeft") setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
      if (e.key === "ArrowRight") setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [view]);

  // Handy: im Kalender nach links wischen = nächster Monat, nach rechts = voriger
  useSwipe({
    enabled: view === "kalender",
    onLeft: () => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1)),
    onRight: () => setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1)),
  });

  const { data: events, isPending } = useQuery({
    queryKey: ["market_events"],
    queryFn: async () => {
      const supabase = getSupabaseClient();
      const [{ data, error }, { data: blocked }] = await Promise.all([
        supabase.from("market_events").select("*").limit(2000),
        supabase.from("companies").select("id").eq("contact_blocked", true),
      ]);
      if (error) throw error;
      // Events von Firmen mit Kontaktsperre (z. B. Nationalfeiertage gesperrter Länder) nicht anzeigen
      const blockedIds = new Set((blocked ?? []).map((c) => c.id));
      return ((data ?? []) as MarketEvent[]).filter(
        (e) => !(e.organiser_company_id && blockedIds.has(e.organiser_company_id)) && !(e.agency_company_id && blockedIds.has(e.agency_company_id)),
      );
    },
  });

  const { data: deals } = useQuery({
    queryKey: ["calendar-deals"],
    queryFn: async () => {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase
        .from("deals")
        .select("id,name,event_date,stage,company_id,location_company_id")
        .not("event_date", "is", null)
        .is("archived_at", null);
      if (error) throw error;
      const ids = [...new Set((data ?? []).flatMap((d) => [d.company_id, d.location_company_id]).filter(Boolean))];
      const { data: companies } = ids.length
        ? await supabase.from("companies").select("id,name").in("id", ids)
        : { data: [] as { id: number; name: string }[] };
      const names = new Map((companies ?? []).map((c) => [c.id, c.name]));
      return (data ?? []).map((d) => ({
        id: d.id,
        name: d.name,
        event_date: d.event_date,
        stage: d.stage,
        company: names.get(d.company_id) ?? null,
        location: d.location_company_id ? (names.get(d.location_company_id) ?? null) : null,
      })) as CalendarDeal[];
    },
  });

  const { data: tasks } = useQuery({
    queryKey: ["calendar-tasks"],
    queryFn: async () => {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase
        .from("tasks")
        .select("id,type,text,due_date,contact_id,sales_id")
        .is("done_date", null)
        .not("due_date", "is", null)
        .limit(1000);
      if (error) throw error;
      const contactIds = [...new Set((data ?? []).map((t) => t.contact_id))];
      const [{ data: contacts }, { data: sales }] = await Promise.all([
        contactIds.length
          ? supabase.from("contacts").select("id,first_name,last_name,company_id").in("id", contactIds)
          : Promise.resolve({ data: [] as { id: number; first_name: string; last_name: string; company_id: number | null }[] }),
        supabase.from("sales").select("id,first_name"),
      ]);
      const companyIds = [...new Set((contacts ?? []).map((c) => c.company_id).filter(Boolean))];
      const { data: companies } = companyIds.length
        ? await supabase.from("companies").select("id,name").in("id", companyIds)
        : { data: [] as { id: number; name: string }[] };
      const companyName = new Map((companies ?? []).map((c) => [c.id, c.name]));
      const contactById = new Map((contacts ?? []).map((c) => [c.id, c]));
      const saleName = new Map((sales ?? []).map((x) => [x.id, x.first_name]));
      return (data ?? []).map((t) => {
        const c = contactById.get(t.contact_id);
        const person = c ? `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() : "Kontakt";
        const company = c?.company_id ? companyName.get(c.company_id) : null;
        return {
          id: t.id,
          type: t.type,
          text: t.text,
          due: String(t.due_date).slice(0, 10),
          contact_id: t.contact_id,
          who: company ? `${person} (${company})` : person,
          owner: saleName.get(t.sales_id) ?? "",
        } as CalendarTask;
      });
    },
  });

  const filteredTasks = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (tasks ?? []).filter((t) => !term || [t.text, t.who].some((v) => v?.toLowerCase().includes(term)));
  }, [tasks, search]);

  const filteredDeals = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (deals ?? []).filter((d) => !term || [d.name, d.company, d.location].some((v) => v?.toLowerCase().includes(term)));
  }, [deals, search]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["market_events"] });

  const groups = useMemo(() => {
    const now = today();
    const term = search.trim().toLowerCase();
    const filtered = (events ?? [])
      .filter((e) => {
        const plan = planDate(e);
        const past = e.start_date ? (e.end_date ?? e.start_date) < now : !plan;
        if (scope === "kommend" && (past || e.status === "kein-thema")) return false;
        if (scope === "offen" && !["ansprechen", "angefragt"].includes(e.status)) return false;
        if (scope === "vergangen" && !past) return false;
        if (category && e.category !== category) return false;
        if (!showNationalDays && !term && isNationalDay(e)) return false;
        if (term && ![e.name, e.location, e.organiser, e.agency].some((v) => v?.toLowerCase().includes(term))) return false;
        return true;
      })
      .sort((a, b) => {
        const da = planDate(a) ?? "9999";
        const db = planDate(b) ?? "9999";
        return scope === "vergangen" ? db.localeCompare(da) : da.localeCompare(db);
      });
    type Item =
      | { kind: "event"; date: string | null; event: MarketEvent }
      | { kind: "deal"; date: string; deal: CalendarDeal }
      | { kind: "task"; date: string; task: CalendarTask };
    const items: Item[] = filtered.map((e) => ({ kind: "event", date: planDate(e), event: e }));
    if (!category && scope !== "offen") {
      for (const d of filteredDeals) {
        const past = d.event_date < now;
        if ((scope === "kommend" && past) || (scope === "vergangen" && !past)) continue;
        items.push({ kind: "deal", date: d.event_date, deal: d });
      }
      for (const t of filteredTasks) {
        // Überfällige Aufgaben bleiben bei „Kommend“ sichtbar, damit sie nicht untergehen
        if (scope === "vergangen") continue;
        items.push({ kind: "task", date: t.due < now && scope === "kommend" ? now : t.due, task: t });
      }
    }
    items.sort((a, b) => {
      const da = a.date ?? "9999";
      const db = b.date ?? "9999";
      const cmp = scope === "vergangen" ? db.localeCompare(da) : da.localeCompare(db);
      return cmp || (a.kind === "deal" ? -1 : 1);
    });
    const map = new Map<string, Item[]>();
    for (const item of items) {
      const key = monthLabel(item.date);
      map.set(key, [...(map.get(key) ?? []), item]);
    }
    return [...map.entries()];
  }, [events, filteredDeals, filteredTasks, scope, category, search, showNationalDays]);

  const count = groups.reduce((n, [, list]) => n + list.length, 0);

  const calendarEvents = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (events ?? []).filter(
      (e) =>
        (!category || e.category === category) &&
        (showNationalDays || !!term || !isNationalDay(e)) &&
        (!term || [e.name, e.location, e.organiser, e.agency].some((v) => v?.toLowerCase().includes(term))),
    );
  }, [events, category, search, showNationalDays]);
  const selected = (events ?? []).find((e) => e.id === selectedId) ?? null;

  return (
    <div className="mt-4 max-w-[1800px] mx-auto">
      <Card>
        <CardContent className="space-y-4">
          <div>
            <div>
              <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
                <CalendarDays className="h-5 w-5" /> Events
              </h1>
              <p className="text-sm text-muted-foreground">
                Unsere Aufträge (★) und Berliner Awards, Galas, Premieren und Empfänge als Anlässe für die Akquise. Graue Termine sind aus der letzten Ausgabe
                abgeleitet und noch nicht bestätigt.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" className="h-9" onClick={() => setAdding(!adding)}>
              <Plus className="h-4 w-4" /> Event
            </Button>
            <div className="flex gap-1 rounded-full bg-muted p-1">
              <button
                type="button"
                onClick={() => setView("kalender")}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm cursor-pointer ${view === "kalender" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
              >
                <CalendarDays className="h-3.5 w-3.5" /> Kalender
              </button>
              <button
                type="button"
                onClick={() => setView("liste")}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm cursor-pointer ${view === "liste" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
              >
                <List className="h-3.5 w-3.5" /> Liste
              </button>
            </div>
            {view === "liste" && (
            <div className="flex gap-1 rounded-full bg-muted p-1">
              {(
                [
                  ["kommend", "Kommend"],
                  ["offen", "In Arbeit"],
                  ["vergangen", "Vergangen"],
                  ["alle", "Alle"],
                ] as [Scope, string][]
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setScope(value)}
                  className={`rounded-full px-3 py-1 text-sm cursor-pointer ${scope === value ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            )}
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
              <option value="">Alle Kategorien</option>
              {Object.entries(CATEGORIES).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={toggleNationalDays}
              aria-pressed={showNationalDays}
              title="Nationalfeiertage der Botschaften ein- oder ausblenden (bei der Suche immer dabei)"
              className={`inline-flex h-9 items-center gap-2 rounded-md border px-3 text-sm cursor-pointer ${showNationalDays ? "border-primary bg-primary/10 text-foreground" : "border-input bg-background text-muted-foreground"}`}
            >
              <span className={`relative inline-block h-4 w-7 rounded-full transition-colors ${showNationalDays ? "bg-primary" : "bg-muted-foreground/30"}`}>
                <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${showNationalDays ? "left-3.5" : "left-0.5"}`} />
              </span>
              Nationalfeiertage
            </button>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suchen: Event, Location, Veranstalter, Agentur …"
              className="h-9 min-w-48 flex-1 rounded-md border border-input bg-background px-3 text-sm md:w-80 md:flex-none"
            />
          </div>

          {adding && (
            <NewEventForm
              onDone={() => {
                setAdding(false);
                refresh();
              }}
            />
          )}

          {isPending ? (
            <p className="py-6 text-sm text-muted-foreground">Wird geladen …</p>
          ) : view === "kalender" ? (
            <div className="space-y-4">
              <MonthGrid events={calendarEvents} deals={filteredDeals} tasks={filteredTasks} month={month} onMonth={setMonth} selectedId={selectedId} onSelect={setSelectedId} />
              {selected && <EventRow key={selected.id} event={selected} onChanged={refresh} defaultOpen />}
            </div>
          ) : count ? (
            <div className="space-y-6">
              <p className="text-xs text-muted-foreground">{count} Einträge</p>
              {groups.map(([month, list]) => (
                <section key={month}>
                  <h2 className="font-display text-lg font-semibold capitalize">{month}</h2>
                  {list.map((item) =>
                    item.kind === "deal" ? (
                      <DealRow key={`deal-${item.deal.id}`} deal={item.deal} />
                    ) : item.kind === "task" ? (
                      <TaskRow key={`task-${item.task.id}`} task={item.task} />
                    ) : (
                      <EventRow key={item.event.id} event={item.event} onChanged={refresh} />
                    ),
                  )}
                </section>
              ))}
            </div>
          ) : (
            <p className="py-6 text-sm text-muted-foreground">Keine Events in dieser Ansicht.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

EventCalendar.path = "/events";
